import { useEffect, useState } from "react";
import { API_REGIONS, ChannelRepository, Client, UserRepository } from '@amityco/ts-sdk';
import { createTextMessage, IChannel, UPTIME_BOT_ID } from "../services/amityService";
import { fetchRoutingUserProfileId, fetchUserId } from "../api/fetchUser";
import { useSelector } from "react-redux";
import { getUserProfile } from "../configuration/login/loginSlice";

export type ChatView = "list" | "chat" | "newChat" | "appointments" | "chameVolks";

function useChatMessages() {
  const [loadingChats, setLoadingChats] = useState<boolean>(false);
  const [amityChannels, setAmityChannels] = useState<IChannel[]>([]);
  const [channel, setChannel] = useState<IChannel | null>(null);
  const [showSidebar, setShowSidebar] = useState<boolean>(false);
  const [currentView, setCurrentView] = useState<ChatView>("list");
  const [userId, setUserId] = useState<string | undefined>();
  const userProfile = useSelector(getUserProfile);
  const [usersData, setUsersData] = useState<{ [key: string]: any }>({});

  const sessionHandler: Amity.SessionHandler = {
    sessionWillRenewAccessToken(renewal: Amity.AccessTokenRenewal) {
      renewal.renew();
    },
  };

  const fetchUsersData = async (userIds: string[]): Promise<{ [key: string]: any }> => {
    const uniqueUserIds = [...new Set(userIds)];

    const usersData: { [key: string]: any } = {};

    try {
      if (uniqueUserIds.length > 0) {
        const usersResponse = await UserRepository.getUserByIds(uniqueUserIds.filter(Boolean));
        for (const user of usersResponse.data) {
          usersData[user.userId] = {
            userId: user.userId,
            displayName: user.metadata?.displayName || (user.metadata?.givenName && user.metadata?.familyName
              ? `${user.metadata.givenName} ${user.metadata.familyName}`
              : null),
            profileImage: user.metadata?.imageProfile?.fileUrl || undefined,
          };
        }
      }

      return usersData;
    } catch (error) {
      console.error('Error fetching users data:', error);
      return {};
    }
  };

  useEffect(() => {
    let unsubscriber: (() => void) | null = null;

    const init = async () => {
      try {
        Client.getActiveClient()
      } catch (error) {
        Client.createClient(import.meta.env.VITE_AMITY_API_KEY, API_REGIONS.US);
      }

      setLoadingChats(true);

      const userId = await fetchUserId(userProfile);

      let profileId = await fetchRoutingUserProfileId();
      if (!profileId) {
        profileId = userId;
      }

      if (!profileId) {
        console.error('Failed to fetch user profile ID');
        setLoadingChats(false);
        return;
      }

      setUserId(profileId);

      try {
        const session = await Client.login(
          { userId: profileId, displayName: profileId },
          sessionHandler,
        );

        const token = Client.getActiveClient().token?.accessToken;

        if (token) {
          sessionStorage.setItem('amity_access_token', token);
        }

        const activeClient = Client.getActiveClient();
        if (!activeClient) {
          console.error('No active Amity client found');
          setLoadingChats(false);
          return;
        }

        unsubscriber = ChannelRepository.getChannels(
          { membership: 'member', limit: 100 },
          async ({ data: channels, onNextPage, hasNextPage, loading, error }) => {
            if (error) {
              console.error('Error fetching channels:', error);
              setLoadingChats(false);
            }
            if (loading) {
              setLoadingChats(true);
            }
            if (channels) {
              const allUserIds: string[] = [];
              channels.forEach((channel: any) => {
                if (channel.metadata?.users && Array.isArray(channel.metadata.users)) {
                  allUserIds.push(...channel.metadata.users);
                }
              });

              const usersData = await fetchUsersData(allUserIds);
              setUsersData(usersData);
              const processedChannels = channels
                .filter((channel: any) => channel.type === 'community' && channel.metadata?.users?.length)
                .map((channel: any) => {
                  const members = (channel.metadata?.users
                    ?.map((userId: string) => {
                      const volkinho = usersData[userId]?.userId === UPTIME_BOT_ID ? {
                        userId: userId,
                        displayName: 'Volkinho',
                        profileImage: '/assets/images/volkinho.svg',
                      } : undefined;

                      return volkinho || {
                        userId: userId,
                        displayName: usersData[userId]?.displayName === UPTIME_BOT_ID ? 'Volkinho' : (usersData[userId]?.displayName || undefined),
                        profileImage: usersData[userId]?.profileImage || undefined,
                      }
                    }) || [])
                    .filter((member: any) =>
                      member.displayName &&
                      member.userId !== profileId &&
                      (member.userId !== member.displayName || member.userId === UPTIME_BOT_ID)
                    );

                  return {
                    id: channel._id,
                    lastActivity: new Date(channel.lastActivity || new Date()),
                    displayName: (channel.metadata?.group && channel.metadata?.chatName) || members.filter((member: any) => member.displayName).map((member: any) => member.displayName).join(', '),
                    userId: members.length === 1 ? members[0].userId : undefined,
                    hasUnreadMessage: channel.subChannelsUnreadCount > 0,
                    members: members,
                    profileImage: members.length === 1 ? members[0].profileImage : undefined,
                    group: channel.metadata?.group,
                  }
                })
                .filter((channel: any) => channel.displayName)
                // Remove duplicated channels based on userId, keeping the most recent one
                // Apenas para canais individuais (não grupos)
                .reduce((acc: any[], current: any) => {
                  // Se for um grupo, sempre adiciona (não deduplica)
                  if (current.group) {
                    acc.push(current);
                    return acc;
                  }

                  // Para canais individuais, verifica duplicatas por userId
                  const existingIndex = acc.findIndex((item: any) =>
                    !item.group && item.userId === current.userId
                  );

                  if (existingIndex === -1) {
                    acc.push(current);
                  } else {
                    const existing = acc[existingIndex];
                    if (current.lastActivity > existing.lastActivity) {
                      acc[existingIndex] = current;
                    }
                  }

                  return acc;
                }, []);

              setAmityChannels(processedChannels);
              setLoadingChats(false);
            }
          },
        );

        // await updateUserName(profileId, userData?.firstName, userData?.lastName, userData?.accountId);
      } catch (error) {
        console.error(error);
        setLoadingChats(false);
      }
    }

    init();

    // Cleanup function para desinscrever quando o componente for desmontado
    return () => {
      if (unsubscriber) {
        unsubscriber();
      }
    };
  }, [userProfile]);

  const handleOpenMessages = (channel: IChannel) => {
    setChannel(channel);
    setCurrentView("chat");
  };

  const handleBackToList = () => {
    setChannel(null);
    setCurrentView("list");
  };

  const handleNewChat = () => setCurrentView("newChat");

  const handleOpenScheduling = () => setCurrentView("appointments");

  const handleOpenChameVolks = () => setCurrentView("chameVolks");

  const toggleSidebar = () => setShowSidebar(!showSidebar);

  return {
    loadingChats,
    amityChannels,
    channel,
    showSidebar,
    currentView,
    userId,
    usersData,
    handleOpenMessages,
    handleBackToList,
    handleNewChat,
    handleOpenScheduling,
    handleOpenChameVolks,
    toggleSidebar
  };
}

export default useChatMessages; 